import { pluralize } from "@/utils/strings";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";

const rtf = new Intl.RelativeTimeFormat("en", {
    style: "narrow",
});
export function timeAgo(date: Date, format = rtf) {
    const millis = date.getTime();
    const diffToNowInSeconds = Math.round((millis - Date.now()) / 1000);

    const thresholds: [number, Intl.RelativeTimeFormatUnit][] = [
        [60, "second"],
        [60 * 60, "minute"],
        [60 * 60 * 24, "hour"],
        [60 * 60 * 24 * 7, "day"],
        [60 * 60 * 24 * 30, "week"],
        [60 * 60 * 24 * 365, "month"],
        [Infinity, "year"],
    ];

    const index = thresholds.findIndex(([second]) => second > Math.abs(diffToNowInSeconds));
    const unit = thresholds[index];

    const divisor = thresholds[index - 1] ? thresholds[index - 1][0] : 1;

    return format.format(Math.round(diffToNowInSeconds / divisor), unit[1]);
}

const mediumTime = Intl.DateTimeFormat("en", {
    timeStyle: "medium",
});
export function formatMediumTime(date: Date) {
    return mediumTime.format(date);
}

const absoluteDateTime = Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
});

export function formatAbsoluteDateTime(date: Date) {
    return absoluteDateTime.format(date);
}

export function duration(seconds: number, style: "short" | "long" = "short", precision: "base" | "extended" = "base") {
    if (seconds < 60) {
        const value = Math.ceil(Math.max(seconds, 0));
        if (style === "short") {
            return `${value}s`;
        }
        return `${value} ` + pluralize(value, "second");
    }

    // Helper function to format a time unit
    const formatUnit = (value: number, shortUnit: string, longUnit: string) => {
        if (value <= 0) return "";

        const formattedValue = new Intl.NumberFormat("en-US", {
            minimumIntegerDigits: 1,
            maximumFractionDigits: 0,
        }).format(value);

        if (style === "short") {
            return `${formattedValue}${shortUnit} `;
        }
        return `${formattedValue} ${pluralize(value, longUnit)} `;
    };

    if (precision === "base") {
        // Base behavior: only handle up to hours
        const hours = Math.floor(seconds / 3600);
        const remainingMinutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = (seconds % 3600) % 60;

        const formattedDuration =
            formatUnit(hours, "h", "hour") +
            formatUnit(remainingMinutes, "m", "minute") +
            formatUnit(remainingSeconds, "s", "second");

        return formattedDuration.trim();
    }

    // Extended behavior: handle weeks and days
    const weeks = Math.floor(seconds / (7 * 24 * 3600));
    const remainingDays = Math.floor((seconds % (7 * 24 * 3600)) / (24 * 3600));
    const remainingHours = Math.floor((seconds % (24 * 3600)) / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = (seconds % 3600) % 60;

    const formattedDuration =
        formatUnit(weeks, "w", "week") +
        formatUnit(remainingDays, "d", "day") +
        formatUnit(remainingHours, "h", "hour") +
        formatUnit(remainingMinutes, "m", "minute") +
        formatUnit(remainingSeconds, "s", "second");

    return formattedDuration.trim();
}

export const formatSeconds = (time: number, style: "short" | "long" = "short") => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    if (hours < 1) {
        if (style === "short") {
            return `${minutes}m`;
        }
        return `${minutes} ` + pluralize(minutes, "minute");
    }

    if (style === "short") {
        return `${hours.toLocaleString()}h`;
    }
    return `${hours.toLocaleString()} ` + pluralize(hours, "hour");
};

/**
 * Formats a date in a short format (e.g. "Jan 1, 2024" or "Jan 1"). *
 * @param date - The date to format.
 * @param includeYear - Whether to include the year in the format.
 * @returns The formatted date string.
 */
export const formatShortDate = (date: Date, includeYear = false): string => {
    if (!date) return "";

    // Check if date is valid
    if (isNaN(date.getTime())) {
        return "";
    }

    const options: Intl.DateTimeFormatOptions = {
        month: "short",
        day: "numeric",
    };

    if (includeYear) {
        options.year = "numeric";
    }

    return new Intl.DateTimeFormat("en-US", options).format(date);
};

export const durationBetweenTimestamps = (
    start: PlainMessage<Timestamp>,
    end: PlainMessage<Timestamp>,
    style: "short" | "long" = "short",
    precision: "base" | "extended" = "base",
): string => {
    const startDate = new Timestamp(start).toDate();
    const endDate = new Timestamp(end).toDate();
    return durationBetweenDates(startDate, endDate, style, precision);
};

export const durationBetweenDates = (
    start: Date,
    end: Date,
    style: "short" | "long" = "short",
    precision: "base" | "extended" = "base",
): string => {
    const durationInSeconds = Math.abs(end.getTime() - start.getTime()) / 1000;
    return duration(durationInSeconds, style, precision);
};

/**
 * Formats a date as "Today, HH:MM" or "MMM DD, HH:MM" (e.g., "Today, 08:00" or "Sept 16, 06:00")
 * @param date - The date to format
 * @returns The formatted date string
 */
export const formatDateWithTime = (date: Date): string => {
    if (!date || isNaN(date.getTime())) {
        return "";
    }

    const now = new Date();
    const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    const time = timeFormatter.format(date);

    if (isToday) {
        return `Today, ${time}`;
    }

    const dateFormatter = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
    });

    return `${dateFormatter.format(date)}, ${time}`;
};
